import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { STATUS } from "@/lib/api";

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay }}>
    {children}
  </motion.div>
);

export const Eyebrow = ({ children, light }) => (
  <p className={`text-xs uppercase tracking-[0.22em] font-bold ${light ? "text-sky-300" : "text-[#0096C7]"}`}>{children}</p>
);

export const SectionHead = ({ eyebrow, title, sub, light, className = "" }) => (
  <div className={`max-w-3xl ${className}`}>
    {eyebrow && <Eyebrow light={light}>{eyebrow}</Eyebrow>}
    <h2 className={`font-display uppercase font-bold text-3xl sm:text-4xl lg:text-5xl leading-[1.02] mt-3 ${light ? "text-white" : "text-[#03045E]"}`}>{title}</h2>
    {sub && <p className={`mt-4 text-base md:text-lg ${light ? "text-sky-100" : "text-slate-600"}`}>{sub}</p>}
  </div>
);

export const PageHero = ({ eyebrow, title, script, sub, children, testid }) => (
  <section className="sky-bg relative overflow-hidden" data-testid={testid}>
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-16 md:pt-20 md:pb-20 relative">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h1 className="font-display uppercase font-bold text-4xl sm:text-5xl lg:text-6xl text-[#03045E] leading-[0.98] mt-3 max-w-4xl">{title}</h1>
      {script && <p className="font-script text-3xl md:text-4xl text-[#0077B6] mt-3">{script}</p>}
      {sub && <p className="mt-5 text-base md:text-lg text-slate-700 max-w-2xl">{sub}</p>}
      {children}
    </div>
    <Wave />
  </section>
);

export const Wave = ({ fill = "#F4F9FC", className = "" }) => (
  <svg className={`absolute bottom-0 left-0 w-full h-10 md:h-14 ${className}`} viewBox="0 0 1440 80" preserveAspectRatio="none" aria-hidden>
    <path d="M0,40 C240,80 480,0 720,30 C960,60 1200,10 1440,40 L1440,80 L0,80 Z" fill={fill} />
  </svg>
);

export const StatusPill = ({ status, testid }) => {
  const s = STATUS[status] || STATUS.nominated;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider text-white" style={{ background: s.color }}>
      <span className="w-1.5 h-1.5 rounded-full bg-white/90" /> {s.label}
    </span>
  );
};

export const Bar = ({ value, max, color = "#0096C7", testid }) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden" data-testid={testid}>
      <motion.div className="h-full rounded-full" style={{ background: color }} initial={{ width: 0 }} whileInView={{ width: `${pct}%` }} viewport={{ once: true }} transition={{ duration: 0.9 }} />
    </div>
  );
};

export const CTAButton = ({ to, children, variant = "navy", testid, className = "" }) => {
  const styles = {
    navy: "bg-[#03045E] text-white hover:bg-[#0077B6]",
    orange: "bg-[#FF6B35] text-white hover:bg-[#e85a26]",
    light: "bg-white text-[#03045E] hover:bg-sky-50 border border-sky-200",
  };
  return (
    <Link to={to} data-testid={testid} className={`group inline-flex items-center gap-2 rounded-full px-6 py-3 font-bold uppercase tracking-wide text-sm transition-colors duration-200 ${styles[variant]} ${className}`}>
      {children} <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
    </Link>
  );
};

export const SourceNote = ({ className = "" }) => (
  <p className={`text-xs text-slate-500 ${className}`}>
    Source: CDC Drowning Facts, updated Jan. 27, 2026. Annual estimates, not a live counter. <Link to="/data" className="underline hover:text-[#0077B6]" data-testid="source-note-data-link">See the Data Center</Link>.
  </p>
);

export const Empty = ({ children, testid }) => (
  <div data-testid={testid} className="rounded-2xl border-2 border-dashed border-sky-200 bg-white/60 p-8 text-center text-slate-600">{children}</div>
);
